import { C } from './whoop-theme';

/**
 * SVG visualizations ported from the design mockup. These are deliberately
 * opaque/presentational — the series come from `gen`, a deterministic generator
 * (no randomness) so sparklines render identically on every load.
 */

export function gen(n: number, base: number, amp: number, trend: number): number[] {
  return Array.from(
    { length: n },
    (_, i) =>
      Math.round(
        (base + trend * i + amp * Math.sin(i * 0.7) + amp * 0.45 * Math.sin(i * 1.9 + 1)) * 10,
      ) / 10,
  );
}

export function RecoveryRing({ pct, color }: { pct: number; color: string }) {
  const size = 140;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - pct / 100);
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ display: 'block' }}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="rgba(255,255,255,0.08)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 1s cubic-bezier(.4,0,.2,1)' }}
      />
      <text
        x={size / 2}
        y={size / 2 - 3}
        textAnchor="middle"
        fill={C.text}
        style={{ font: '700 34px "Hanken Grotesk",sans-serif' }}
      >
        {pct}%
      </text>
      <text
        x={size / 2}
        y={size / 2 + 19}
        textAnchor="middle"
        fill={C.text2}
        style={{ font: '500 10px "JetBrains Mono",monospace', letterSpacing: '0.16em' }}
      >
        RECOVERY
      </text>
    </svg>
  );
}

export function Spark({
  series,
  color,
  baseline,
}: {
  series: number[];
  color: string;
  baseline?: number | null;
}) {
  const w = 96;
  const ht = 32;
  const pad = 3;
  const hasBaseline = baseline != null;
  const min = Math.min(...series, hasBaseline ? baseline : Infinity);
  const max = Math.max(...series, hasBaseline ? baseline : -Infinity);
  const range = max - min || 1;
  const X = (i: number) => pad + (i * (w - 2 * pad)) / (series.length - 1);
  const Y = (v: number) => ht - pad - ((v - min) / range) * (ht - 2 * pad);
  const d = series.map((v, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`).join(' ');
  const last = series.at(-1) ?? 0;
  const lx = X(series.length - 1);
  const ly = Y(last);
  return (
    <svg viewBox={`0 0 ${w} ${ht}`} width={w} height={ht} style={{ display: 'block' }}>
      {hasBaseline && (
        <line
          x1={pad}
          x2={w - pad}
          y1={Y(baseline)}
          y2={Y(baseline)}
          stroke="rgba(255,255,255,0.2)"
          strokeWidth={1}
          strokeDasharray="3 3"
        />
      )}
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lx} cy={ly} r={2.4} fill={color} />
    </svg>
  );
}

/** Predicted marathon-finish trajectory: history line, dashed projection + band, goal line. */
export function TrajectoryChart() {
  const W = 720;
  const H = 250;
  const padL = 50;
  const padR = 92;
  const padT = 16;
  const padB = 30;
  const histo = [251, 249, 250, 246, 244, 241, 238, 236, 233];
  const proj = [233, 232, 230, 229, 228, 227, 226, 226];
  const lower = [233, 231, 228, 226, 224, 223, 222, 221];
  const upper = [233, 234, 233, 233, 232, 232, 231, 231];
  const ymin = 219;
  const ymax = 253;
  const xf = (i: number) => padL + (i * (W - padL - padR)) / 15;
  const yf = (v: number) => padT + ((v - ymin) / (ymax - ymin)) * (H - padT - padB);

  const hl = histo
    .map((v, i) => `${i ? 'L' : 'M'}${xf(i).toFixed(1)} ${yf(v).toFixed(1)}`)
    .join(' ');
  const pl = proj
    .map((v, j) => `${j ? 'L' : 'M'}${xf(8 + j).toFixed(1)} ${yf(v).toFixed(1)}`)
    .join(' ');

  let band = `M${xf(8).toFixed(1)} ${yf(upper[0] ?? 0).toFixed(1)}`;
  upper.forEach((v, j) => {
    if (j) band += ` L${xf(8 + j).toFixed(1)} ${yf(v).toFixed(1)}`;
  });
  for (let j = lower.length - 1; j >= 0; j--) {
    band += ` L${xf(8 + j).toFixed(1)} ${yf(lower[j] ?? 0).toFixed(1)}`;
  }
  band += ' Z';

  const ty = yf(225);
  const gridLines = [221, 228, 235, 242, 249];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', maxWidth: '100%' }}>
      {gridLines.map((v, i) => (
        <g key={`g${i}`}>
          <line
            x1={padL}
            x2={W - padR}
            y1={yf(v)}
            y2={yf(v)}
            stroke="rgba(255,255,255,0.05)"
            strokeWidth={1}
          />
          <text
            x={padL - 8}
            y={yf(v) + 3}
            textAnchor="end"
            fill={C.text3}
            style={{ font: '500 9px "JetBrains Mono",monospace' }}
          >
            {`3:${Math.floor(v / 60) === 3 ? String(v - 180).padStart(2, '0') : '??'}`}
          </text>
        </g>
      ))}
      <path d={band} fill="rgba(181,155,255,0.1)" stroke="none" />
      <line
        x1={padL}
        x2={W - padR}
        y1={ty}
        y2={ty}
        stroke={C.good}
        strokeWidth={1.5}
        strokeDasharray="5 4"
      />
      <text
        x={W - padR + 6}
        y={ty + 3}
        fill={C.good}
        style={{ font: '600 10px "JetBrains Mono",monospace' }}
      >
        Goal 3:45
      </text>
      <line
        x1={xf(8)}
        x2={xf(8)}
        y1={padT}
        y2={H - padB}
        stroke="rgba(255,255,255,0.16)"
        strokeWidth={1}
        strokeDasharray="2 3"
      />
      <path
        d={hl}
        fill="none"
        stroke={C.text2}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d={pl}
        fill="none"
        stroke={C.accent}
        strokeWidth={2}
        strokeDasharray="6 4"
        strokeLinecap="round"
      />
      <circle cx={xf(8)} cy={yf(233)} r={4.5} fill={C.accent} stroke="#0c0c0e" strokeWidth={2} />
      <text
        x={xf(8)}
        y={yf(233) - 12}
        textAnchor="middle"
        fill={C.text}
        style={{ font: '700 11px "Hanken Grotesk",sans-serif' }}
      >
        3:53 now
      </text>
      <text
        x={xf(0)}
        y={H - padB + 16}
        textAnchor="start"
        fill={C.text3}
        style={{ font: '500 9px "JetBrains Mono",monospace' }}
      >
        8 wks ago
      </text>
      <text
        x={xf(15)}
        y={H - padB + 16}
        textAnchor="end"
        fill={C.text3}
        style={{ font: '500 9px "JetBrains Mono",monospace' }}
      >
        race day
      </text>
    </svg>
  );
}

/** Diverging bars showing each metric's deviation from its 30-day baseline. */
export function BaselineBars() {
  const items = [
    { label: 'HRV', pct: -10, color: C.warn },
    { label: 'Resting HR', pct: 6, color: C.warn },
    { label: 'Sleep duration', pct: -2, color: C.text2 },
    { label: 'Respiratory rate', pct: 1, color: C.good },
    { label: 'Weekly strain', pct: 9, color: C.accent },
  ];
  const W = 440;
  const rowH = 34;
  const H = items.length * rowH + 8;
  const cx = W * 0.52;
  const max = 12;
  const scale = (W - cx - 70) / max;
  const scaleL = (cx - 90) / max;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', maxWidth: '520px' }}>
      <line x1={cx} x2={cx} y1={4} y2={H - 4} stroke="rgba(255,255,255,0.14)" strokeWidth={1} />
      {items.map((it, i) => {
        const y = 8 + i * rowH;
        const w = Math.abs(it.pct) * (it.pct < 0 ? scaleL : scale);
        const x = it.pct < 0 ? cx - w : cx;
        return (
          <g key={i}>
            <text
              x={8}
              y={y + rowH / 2 + 3}
              fill={C.text}
              style={{ font: '500 12px "Hanken Grotesk",sans-serif' }}
            >
              {it.label}
            </text>
            <rect
              x={x}
              y={y + 6}
              width={w}
              height={rowH - 18}
              rx={3}
              fill={it.color}
              opacity={0.85}
            />
            <text
              x={it.pct < 0 ? x - 6 : x + w + 6}
              y={y + rowH / 2 + 3}
              textAnchor={it.pct < 0 ? 'end' : 'start'}
              fill={it.color}
              style={{ font: '600 11px "JetBrains Mono",monospace' }}
            >
              {`${it.pct > 0 ? '+' : ''}${it.pct}%`}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
